import { createFileRoute } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SectionHeading } from "@/components/ui-ext/SectionHeading";
import { Picture } from "@/components/media/Picture";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { siteConfig } from "@/config/site";

const ABOUT_FAQS_LD = [

  {
    q: `Is ${siteConfig.founder.name} actually hands-on, or is that just marketing language?`,
    a: `${siteConfig.founder.name} leads the technical audit and strategy on every account personally — he sets the roadmap, reviews the monthly reports before they go out, and joins the weekly working session for active engagements.`,
  },

  {
    q: `How is ${siteConfig.name} different from a typical ${siteConfig.contact.address.city} SEO agency?`,
    a: "We run one documented framework — the eight-stage Optimize Framework — on every engagement, share raw Search Console, GA4, and Semrush access instead of curated slides, and only cite results with a dated, sourced screenshot from a named client.",

  },
  {
    q: `Do you only work with ${siteConfig.contact.address.city} businesses?`,
    a: `No. We're headquartered in ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state}, and it shapes how we think about local SEO, but the majority of our roster is national — home-service brands, automotive shops, security integrators, and B2B SaaS teams across the US.`,

  },
  {
    q: "What size of business do you typically work with?",
    a: "Most clients are small-to-mid-size businesses and B2B teams with a real sales process behind their organic traffic — a phone call, a booked job, or a signed contract, not just a form fill.",
  },
  {
    q: "How long before I see results?",
    a: "The first 90 days are foundation work — technical fixes, entity cleanup, and the first content wave. Early leading indicators (indexation, impressions) typically move inside that window; meaningful ranking and traffic movement usually follows in months three to six, depending on competitive density.",
  },
  {
    q: `Will I work with the founder or get handed to a junior account manager?`,
    a: `You work with a small, senior team led directly by ${siteConfig.founder.name}. We deliberately keep the roster small enough that there's no junior-account-manager layer between you and the person making strategic calls.`,
  },

  {
    q: "What happens if the strategy isn't working?",
    a: "We revisit it at the quarterly strategy reset, and sooner if the monthly report shows the roadmap isn't moving the KPIs we agreed on. Every claim in our reporting is sourced, so underperformance is visible immediately, not hidden behind vanity metrics.",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({ ...pageMeta({
    path: "/about/",
    title: `About ${siteConfig.name} — ${siteConfig.contact.address.city} SEO & AI Search Specialists`,
    description:
      `Meet the team behind ${siteConfig.contact.address.city}'s founder-led SEO agency. Specialists in technical SEO, Local Pack dominance, and AI search engine optimization (GEO).`,

    keywords: [
      `about ${siteConfig.name}`,
      siteConfig.founder.name,

      `${siteConfig.contact.address.city} SEO founder`,
      `SEO consultant ${siteConfig.contact.address.city}`,

      "SEO agency team",
      "AI search consultant",
      "GEO expert",
    ],
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about/" }]),
    faqLd(ABOUT_FAQS_LD),
  ) }),
  component: AboutPage,
});


function AboutPage() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1} className="container-page py-20 md:py-28">
        <SectionHeading
          as="h1"
          eyebrow={`About ${siteConfig.name}`}
          title={`${siteConfig.contact.address.city}'s founder-led SEO and AI search agency.`}
          intro={`${siteConfig.name} is an SEO agency headquartered in ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state}, serving small businesses, home-service brands, and B2B founders across Texas and North America. We were built for operators who are done with agencies that sell rankings without accountability — and want a documented, measurable organic channel instead.`}

        />

        <section className="mt-16 grid gap-10 md:grid-cols-[300px_1fr] items-start">
          <Picture
            variantKey="umair"
            alt=""
            width={300}
            height={360}
            sizes="300px"
            className="block shrink-0"
            imgClassName="rounded-2xl object-cover w-[300px] h-[360px]"
          />
          <div className="space-y-5 text-foreground/90 leading-relaxed">
            <h2 className="font-display text-3xl">{siteConfig.founder.name}, Founder of {siteConfig.name}</h2>
            <p>
              I've spent the last decade building search programs for automotive shops,
              HVAC contractors, physical-security integrators, and B2B SaaS teams —
              disciplines where a lead is a real phone call, a booked job, or a signed

              contract, not a form fill. What I kept seeing was the same pattern: brands
              were sold on rankings but never bought the operating system behind them, so
              wins never compounded and every quarter started from zero.
            </p>
            <p>
              {siteConfig.name} is the opposite of that. Every engagement ships a dated
              roadmap, weekly working sessions, monthly executive-grade reports pulled
              from Google Search Console, Google Analytics 4, and Semrush, and a
              baseline you can benchmark against forever. Sourced screenshots. Named
              clients. No claim without a date and a source.
            </p>

            <p>
              We use the same documented SEO growth process on every account — the same
              eight-step system that helped Foreign Affairs Auto reach roughly 6,100 monthly
              organic visits, Lynx Systems earn 619K impressions in six months, and Carage
              Auto build 137 pages that are actively cited by ChatGPT, Perplexity, and
              Google AI Overviews.
            </p>

          </div>
        </section>

        <section className="mt-20 grid gap-6 md:grid-cols-2">
          <Picture
            variantKey="team"
            alt=""
            className="rounded-2xl overflow-hidden border border-border block"
            imgClassName="w-full h-full object-cover aspect-[4/3]"
            sizes="(min-width: 768px) 50vw, 100vw"
          />
          <Picture
            variantKey="workspace"
            alt=""
            className="rounded-2xl overflow-hidden border border-border block"
            imgClassName="w-full h-full object-cover aspect-[4/3]"
            sizes="(min-width: 768px) 50vw, 100vw"
          />
        </section>




        <section className="mt-24 grid gap-6 md:grid-cols-3">
          {[
            { t: "Founder-led", d: "You work with senior operators, not a rotating cast of juniors." },
            { t: "Sourced results", d: "Every claim comes with a screenshot, a date, and a source." },
            { t: "Compounding systems", d: "We build growth engines, not one-off campaigns." },
          ].map((v) => (
            <div key={v.t} className="surface-card p-6">
              <h3 className="font-display text-xl">{v.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
            </div>
          ))}
        </section>

        <section className="mt-24 max-w-3xl">
          <EyebrowLabel>Who this page is for</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Read this before you book a call.
          </h2>
          <p className="mt-4 text-foreground/85 leading-relaxed">
            This page exists for two kinds of readers: operators trying to work out whether
            {siteConfig.name} is a fit before spending thirty minutes on a strategy call, and

            existing clients who want the full story behind the process we run for them. If
            you're comparing agencies on price alone, we're probably not your fit — we're
            priced for the depth of the work, not the headline rate. If you're comparing on
            whether an agency will actually show its working, keep reading.
          </p>
        </section>

        <section className="mt-20">
          <EyebrowLabel>The Optimize Framework</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl max-w-2xl">
            One documented system, run the same way on every account.
          </h2>
          <p className="mt-4 max-w-2xl text-foreground/85 leading-relaxed">
            We don't improvise a new methodology for every client — that's how agencies lose
            institutional knowledge every time an account manager leaves. The Optimize
            Framework is an eight-stage system that runs identically whether the client is an
            automotive shop in South Austin or a B2B SaaS company selling nationally.
          </p>
          <ol className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              { t: "1. Technical baseline", d: "Full crawl, index coverage, Core Web Vitals, and log-file review before we touch content. You can't optimise what you haven't measured." },
              { t: "2. Entity & schema audit", d: "We map how Google and AI engines currently understand the business — NAP consistency, structured data, and knowledge-graph presence." },
              { t: "3. Keyword & intent mapping", d: "Search-demand research segmented by commercial intent, not just volume, tied to the pages that should rank for each cluster." },
              { t: "4. Content architecture", d: "A topical map that groups pages into hub-and-spoke clusters so authority compounds instead of scattering across orphaned posts." },
              { t: "5. On-page & technical fixes", d: "Shipped in priority order — indexation and crawl blockers first, then rendering and speed, then on-page optimisation." },
              { t: "6. Authority building", d: "Digital PR, citations, and link earning scoped to the client's actual industry, not generic guest-post networks." },
              { t: "7. AI search optimisation", d: "Structuring content so it can be extracted and cited by ChatGPT, Perplexity, and Google AI Overviews — not just ranked in blue links." },
              { t: "8. Measurement & iteration", d: "Monthly reporting from Search Console, GA4, and Semrush, with a documented backlog re-prioritised every cycle." },
            ].map((s) => (
              <li key={s.t} className="surface-card p-6">
                <h3 className="font-display text-lg text-primary">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-20 grid gap-10 md:grid-cols-2">
          <div>
            <EyebrowLabel>Operating principles</EyebrowLabel>
            <h2 className="mt-3 font-display text-3xl">How we run engagements.</h2>
            <ul className="mt-5 space-y-4 text-foreground/85 leading-relaxed">
              <li><strong className="text-foreground">No black boxes.</strong> Clients see the same dashboards we see — Search Console, GA4, and Semrush access is shared, not summarised into a slide deck we control.</li>
              <li><strong className="text-foreground">No vanity metrics.</strong> We report impressions and rankings alongside clicks and conversions, because rankings without clicks aren't results.</li>
              <li><strong className="text-foreground">No indefinite retainers without a plan.</strong> Every engagement has a dated roadmap. If the plan changes, we say why, in writing.</li>
              <li><strong className="text-foreground">No borrowed case studies.</strong> Every result we cite is a named client with a sourced screenshot and a date.</li>
            </ul>
          </div>
          <div>
            <EyebrowLabel>Team model</EyebrowLabel>
            <h2 className="mt-3 font-display text-3xl">Senior, small, and accountable.</h2>
            <p className="mt-5 text-foreground/85 leading-relaxed">
              We deliberately run a smaller roster than a traditional agency. {siteConfig.founder.name} leads

              strategy and technical audits directly on every account; a focused bench of
              content, technical, and digital-PR specialists execute against the roadmap he
              sets. There is no junior-account-manager layer between you and the person making
              the calls — when something needs a decision, you're talking to the person who
              can actually make it.
            </p>
          </div>
        </section>

        <section className="mt-20">
          <EyebrowLabel>Engagement model</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl max-w-2xl">
            What working with us actually looks like, month to month.
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {[
              { t: "Weekly", d: "A working session — not a status call. We review what shipped, what's blocked, and what's next on the roadmap." },
              { t: "Monthly", d: "An executive report pulled from Search Console, GA4, and Semrush: traffic, rankings, indexation health, and content shipped." },
              { t: "Quarterly", d: "A strategy reset — we revisit the topical map, competitive landscape, and whether the KPIs we set still match the business goals." },
            ].map((s) => (
              <div key={s.t} className="surface-card p-6">
                <h3 className="font-display text-lg">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20 max-w-3xl">
          <EyebrowLabel>The first 90 days</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            What happens before results show up.
          </h2>
          <p className="mt-4 text-foreground/85 leading-relaxed">
            SEO has a lag — anyone who promises otherwise is either lying or measuring the
            wrong thing. In the first 90 days, we're not chasing rankings; we're fixing the
            foundation that makes ranking possible. Weeks one and two are the technical and
            entity audit. Weeks three through six are indexation and Core Web Vitals fixes,
            plus the first wave of content built against the topical map. Weeks seven through
            twelve are when the first ranking movement typically becomes visible in Search
            Console — impressions before clicks, mid-page-two keywords before page one. Clients
            who expect page-one rankings in month one are usually looking at the wrong
            timeline; clients who track indexation, impressions, and content velocity in month
            one have the right leading indicators.
          </p>
        </section>

        <section className="mt-20 max-w-3xl">
          <EyebrowLabel>E-E-A-T, in practice</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Experience, expertise, authority, trust — not a checkbox.
          </h2>
          <p className="mt-4 text-foreground/85 leading-relaxed">
            Google's quality guidelines, and increasingly the retrieval logic behind AI
            Overviews and chat-based answers, weight demonstrated experience over generic
            claims. In practice that means author bios with real credentials, first-hand case
            studies instead of paraphrased research, cited sources, and a consistent entity
            footprint across the website, Google Business Profile, and third-party mentions.
            We build this into every client engagement — not as an add-on, but as one of the
            eight stages of the framework above.
          </p>
        </section>


        <section className="mt-20 max-w-3xl">
          <EyebrowLabel>Austin market context</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Why Austin shapes how we think about local SEO.
          </h2>
          <p className="mt-4 text-foreground/85 leading-relaxed">
            Austin is one of the fastest-changing metros in the country for local search —
            new residents, new competitors, and a Google Business Profile landscape that
            shifts every quarter as service areas expand into Round Rock, Cedar Park, and
            Georgetown. Running our own headquarters here means we feel every algorithm
            update on our own local rankings before we ever apply a fix for a client. That
            first-hand exposure to {siteConfig.contact.address.city}'s local pack volatility, review-gating crackdowns,
            and map-pack radius changes feeds directly into how we advise clients in every
            Texas market we serve, not just {siteConfig.contact.address.city} proper.

          </p>
        </section>

        <section className="mt-20 max-w-3xl">
          <EyebrowLabel>Common mistakes we see</EyebrowLabel>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Patterns that stall an in-house or previous-agency programme.
          </h2>
          <ul className="mt-5 space-y-4 text-foreground/85 leading-relaxed list-disc pl-5">
            <li>Publishing content without a topical map, so pages compete with each other instead of compounding authority.</li>
            <li>Treating technical SEO as a one-time audit instead of an ongoing discipline — crawl budgets and Core Web Vitals drift as sites grow.</li>
            <li>Optimising only for Google's blue links while AI Overviews and chat-based answers quietly take an increasing share of top-of-funnel queries.</li>
            <li>Chasing rankings for vanity keywords instead of the commercial-intent terms that actually convert to calls or bookings.</li>
            <li>Signing a retainer with no dated roadmap, so there's no way to tell whether month six looks meaningfully different from month one.</li>
          </ul>
        </section>
        <section className="mt-24 max-w-3xl" aria-labelledby="about-faq-heading">
          <EyebrowLabel>FAQ</EyebrowLabel>
          <h2 id="about-faq-heading" className="mt-3 font-display text-3xl md:text-4xl">
            Questions we get before the first call.
          </h2>
          <dl className="mt-8 space-y-6">
            {ABOUT_FAQS_LD.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
